import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Button } from '@monksflow/monks-ui';
import { FunnelIcon } from 'lucide-react';

import { formatMonth } from '../../../../utils/formatMonth';
import { AnalysisSelect } from '../../../../components';
import { normaliseRange, type MediaLandscapeFilters } from '../../model/filters';
import { BrandIdentity } from '../../../../components';

interface BrandFiltersBrand {
  name: string;
  color: string;
  domain: string | null;
}

interface BrandFiltersProps {
  months: string[];
  brands: BrandFiltersBrand[];
  value: MediaLandscapeFilters;
  /**
   * Bumped by the parent on every apply and clear. `onApply` normalises an inverted range, so
   * the normalised result can equal the applied value; keying the remount on `value` alone
   * would skip it and leave the selects showing the inverted pair the user picked.
   */
  applyCount: number;
  onApply: (filters: MediaLandscapeFilters) => void;
  onClear: () => void;
}

const chipClassName =
  'flex h-9 items-center gap-2 rounded-lg border px-3.5 text-sm transition-colors duration-fast ease-out';
const fieldClassName = 'flex flex-col gap-1';
const fieldLabelClassName = 'text-xs text-muted-foreground';

const BrandFiltersInner = ({ months, brands, value, onApply, onClear }: BrandFiltersProps) => {
  const { t } = useTranslation('analysis');
  const [pending, setPending] = useState(value);
  const fieldId = useId();

  // Ticking every chip is the same scope as ticking none; the KPI cards read it that way too.
  const allSelected = value.advertisers.length === 0 || value.advertisers.length === brands.length;
  // Same equivalence against the uncommitted selection, so the chip cannot contradict the
  // scope line above it while the user is still picking.
  const allPending =
    pending.advertisers.length === 0 || pending.advertisers.length === brands.length;
  const scope = allSelected
    ? t('analysisResult.mediaLandscape.allBrands')
    : t('analysisResult.mediaLandscape.brandCount', {
        selected: value.advertisers.length,
        total: brands.length,
      });
  const range =
    value.from === value.to
      ? formatMonth(value.from, 'numeric')
      : `${formatMonth(value.from, 'numeric')} – ${formatMonth(value.to, 'numeric')}`;

  const monthOptions = months.map((month) => ({
    value: month,
    label: formatMonth(month, 'numeric'),
  }));

  const toggleBrand = (name: string) =>
    setPending((prev) => ({
      ...prev,
      advertisers: prev.advertisers.includes(name)
        ? prev.advertisers.filter((b) => b !== name)
        : [...prev.advertisers, name],
    }));

  const startDateLabel = t('analysisResult.mediaLandscape.startDate');
  const endDateLabel = t('analysisResult.mediaLandscape.endDate');

  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-border-light bg-surface px-4 py-3.5 dark:bg-surface-raised">
      <div className="flex items-center gap-2">
        <FunnelIcon className="size-[15px] text-foreground" />
        <span className="text-sm font-medium text-foreground">
          {t('analysisResult.mediaLandscape.dataFilters')}
        </span>
        <span className="text-xs text-muted-foreground">{`${scope} · ${range}`}</span>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" className="text-primary" onClick={onClear}>
          {t('analysisResult.mediaLandscape.clearAll')}
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-pressed={allPending}
          onClick={() => setPending((prev) => ({ ...prev, advertisers: [] }))}
          className={clsx(
            chipClassName,
            allPending
              ? 'border-primary bg-primary font-medium text-primary-foreground'
              : 'border-border bg-card text-foreground',
          )}
        >
          {t('analysisResult.mediaLandscape.allBrands')}
        </button>
        {brands.map((brand) => (
          <button
            key={brand.name}
            type="button"
            aria-pressed={pending.advertisers.includes(brand.name)}
            onClick={() => toggleBrand(brand.name)}
            className={clsx(
              chipClassName,
              'pl-2',
              pending.advertisers.includes(brand.name)
                ? 'border-primary bg-primary font-medium text-primary-foreground'
                : 'border-border bg-card text-foreground',
            )}
          >
            <BrandIdentity advertiser={brand.name} domain={brand.domain} color={brand.color} />
            {brand.name}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className={fieldClassName}>
          {/* htmlFor/id pairs the label with the Radix trigger; nesting crashes jsx-a11y here. */}
          <label className={fieldLabelClassName} htmlFor={`${fieldId}-from`}>
            {startDateLabel}
          </label>
          <AnalysisSelect
            id={`${fieldId}-from`}
            variant="filter"
            icon="calendar"
            ariaLabel={startDateLabel}
            value={pending.from}
            onValueChange={(from) => setPending((prev) => ({ ...prev, from }))}
            options={monthOptions}
          />
        </div>
        <div className={fieldClassName}>
          <label className={fieldLabelClassName} htmlFor={`${fieldId}-to`}>
            {endDateLabel}
          </label>
          <AnalysisSelect
            id={`${fieldId}-to`}
            variant="filter"
            icon="calendar"
            ariaLabel={endDateLabel}
            value={pending.to}
            onValueChange={(to) => setPending((prev) => ({ ...prev, to }))}
            options={monthOptions}
          />
        </div>
        <div className="flex-1" />
        <Button
          size="lg"
          onClick={() => {
            const [from, to] = normaliseRange(pending.from, pending.to);
            onApply({ ...pending, from, to });
          }}
        >
          {t('analysisResult.mediaLandscape.applyFilters')}
        </Button>
      </div>
    </div>
  );
};

/** Remounting on every apply and clear re-seeds pending state without a `useEffect`. */
export const BrandFilters = (props: BrandFiltersProps) => (
  <BrandFiltersInner
    key={`${props.applyCount}|${props.value.from}|${props.value.to}|${props.value.advertisers.join(',')}`}
    {...props}
  />
);
